import '../ProductBox/ProductBox.css';
import './ProductBoxGeneratePrice.css';
import { ApiProduct, ApiSpecialsProduct, ApiSpecialSuggestions } from '../../../types';
import { FavoriteButton } from '../../Favorites/FavoriteButton';
import { Link } from 'react-router-dom';
import { useFormatting, useTranslation } from '../../../i18n/i18n';
import { Img } from '../../Img/Img';
import { Button } from '../../Form/Button';
import { DeliveryTime } from '../../product/DeliveryTime';
import { PriceTagSimple } from '../../PriceTagSimple/PriceTagSimple';
import { EcoLabels } from '../../EcoLabel/EcoLabels';
import { ProductContainer } from '../../../state/product';
import { useSession } from '../../../state/session';
import { Icon, lock } from '../../Icon/Icon';
import { useEffect, useState } from 'react';

type ProductBoxSpecialPriceProps = {
	product: ApiProduct;
	highlight?: boolean;
	className?: string;
	onClick: (action: 'accept' | 'generate', product: ApiProduct) => void;
	displayLockedUnitil?: boolean;
};

export const ProductBoxGeneratePrice = ({
	product,
	highlight,
	className,
	onClick,
	displayLockedUnitil = false,
}: ProductBoxSpecialPriceProps): JSX.Element => {
	const { id, name, price, campaignPrice, image, perma, ecoLabels, deliveryClass, deliveryNotice } = product;
	const [specialPriceProduct, setSpecialPriceProduct] = useState<ApiSpecialsProduct>();
	const [specialPriceSuggestion, setSpecialPriceSuggestion] = useState<ApiSpecialSuggestions>();
	const { getFinalPrice, specialPricesIndex, getSpecialPriceSuggestion } = ProductContainer.useContainer();
	const isSpecialProduct = !!getSpecialPriceSuggestion(id);
	const { isLoggedIn } = useSession();
	const { asPrice } = useFormatting();
	const size = { width: 292, height: 292 };

	useEffect(() => {
		if (isSpecialProduct) {
			setSpecialPriceSuggestion(getSpecialPriceSuggestion(product.id));
			setSpecialPriceProduct(specialPricesIndex[product.id]);
		}
	}, [product, isSpecialProduct]);

	const SpecialPriceButton = ({
		onClick,
		specialPriceGenerated,
		minPriceReached,
		specialPriceProduct,
		disabled,
	}: {
		onClick: (action: 'accept' | 'generate', product: ApiProduct) => void;
		specialPriceGenerated: boolean;
		minPriceReached: boolean;
		specialPriceProduct?: ApiSpecialsProduct | undefined;
		disabled?: boolean;
	}) => {
		const t = { ...useTranslation().specialPrices, ...useTranslation().product };
		const specialPriceActivated = !!specialPriceProduct ? specialPricesIndex[product.id].customerSpecial : false;
		if (disabled) {
			return <Button className="disabled">{t.buyNow}</Button>;
		}
		if (minPriceReached || specialPriceActivated) {
			return (
				<Button className="choose-special-price-button reached">
					{`${specialPriceActivated ? t.chooseProduct : t.generatePrice}`}
				</Button>
			);
		}
		return specialPriceGenerated ? (
			<Button className="choose-special-price-button" onClick={() => onClick('accept', product)}>
				{t.acceptPrice2}
			</Button>
		) : (
			<Button className="generate-special-price-button" onClick={() => onClick('generate', product)}>
				{t.generatePrice}
			</Button>
		);
	};

	const SpecialPriceBadges = ({
		specialsProduct,
		minPriceReached,
	}: {
		specialsProduct: ApiSpecialsProduct | undefined;
		minPriceReached: boolean;
	}) => {
		const t = useTranslation().specialPrices;
		if (minPriceReached) {
			return (
				<div className="special-price-badges">
					<div className="ribbon special-price reached">{t.minPriceReached}</div>
				</div>
			);
		}
		let priceDiff: string;
		if (!specialsProduct) {
			const specialPriceSuggestion = getSpecialPriceSuggestion(product.id);
			if (specialPriceSuggestion) {
				priceDiff = asPrice(product.price - specialPriceSuggestion.price);
			} else {
				return null;
			}
		} else {
			priceDiff = asPrice(specialsProduct.product.price - specialsProduct.newPrice);
		}

		return (
			<div className="special-price-badges">
				<div className="ribbon special-price">{`${specialPriceProduct?.customerSpecial ? t.alreadyActivated : t.specialPrice}`}</div>
				<div className="ribbon special-price">-{priceDiff}</div>
			</div>
		);
	};

	const LockedUntilDateBadge = ({ lockedUntilDate }: { lockedUntilDate: string }): JSX.Element => {
		return (
			<div className="locked-until-date-badge">
				<Icon shape={lock} size={12} />
				<div>{lockedUntilDate.split(' ')[0]}</div>
			</div>
		);
	};

	const minPriceReached = (specialsProduct: ApiSpecialsProduct | undefined, product: ApiProduct) => {
		if (!specialsProduct) {
			const specialPriceSuggestion = getSpecialPriceSuggestion(product.id);
			if (specialPriceSuggestion && specialPriceSuggestion.price === -1) {
				return true;
			}
		}
		if (specialsProduct) {
			return specialsProduct.newPrice >= product.price || specialsProduct.newPrice === -1;
		}
		return false;
	};
	const minPriceIsReached = minPriceReached(specialPriceProduct, product);

	// true if is ApiSpecialProduct or exists in specials suggestions array
	const specialPriceIsGenerated = !!getSpecialPriceSuggestion(product.id) || !!specialPriceProduct;

	return (
		<product-box
			class={`generate-price ${className ? className : ''} ${highlight ? 'highlight' : product.campaignPrice ? 'campaign' : ''}`}
		>
			<SpecialPriceBadges specialsProduct={specialPriceProduct} minPriceReached={minPriceIsReached} />
			<FavoriteButton id={product.id} />
			{ecoLabels && ecoLabels.length > 0 ? (
				<EcoLabels ecoLabels={ecoLabels} isLoggedIn={isLoggedIn} size={22} />
			) : (
				<div />
			)}
			<Link to={{ pathname: '/' + perma }} target="_blank" className="product-link">
				<Img alt={name} src={image} transparent={highlight} {...size} imgWidth={175} imgHeight={175} />
				<div className="product-name">{name}</div>
				{displayLockedUnitil && isSpecialProduct && specialPriceProduct && (
					<LockedUntilDateBadge lockedUntilDate={specialPriceProduct.lockedUntil} />
				)}
				<div className="price-container">
					<PriceTagSimple
						price={
							specialPriceSuggestion && specialPriceSuggestion.price !== -1
								? specialPriceSuggestion.price
								: getFinalPrice(id, price, 1, campaignPrice)
						}
						isCampaign={campaignPrice > 0 || !!specialPriceSuggestion}
						vatRate={product.vatRate}
						unit={product.unit}
					></PriceTagSimple>
					<DeliveryTime
						deliveryNotice={deliveryNotice}
						deliveryClass={deliveryClass}
						textLength={'short-one-line'}
					/>
				</div>
			</Link>
			<div className="buy-area">
				<SpecialPriceButton
					onClick={onClick}
					specialPriceGenerated={specialPriceIsGenerated}
					minPriceReached={minPriceIsReached}
					specialPriceProduct={specialPriceProduct}
					disabled={specialPriceProduct?.product.deliveryClass === 6}
				/>
			</div>
		</product-box>
	);
};
